import React from 'react';
import { FiArrowRight, FiHeart, FiAward, FiUsers } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Story = () => {
  return (
    <section className="py-12 lg:py-20 bg-gradient-to-br from-emerald-50 to-teal-50 overflow-hidden" id="about">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Image Side */}
          <div className="relative order-first lg:order-last w-full max-w-full" data-aos="fade-right">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl w-full">
              <img
                src="/img/about/about.png"
                alt="Children learning in a warm, nurturing classroom environment"
                className="w-full aspect-[4/3] lg:h-[500px] lg:aspect-auto object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/20 to-transparent"></div>
            </div>

            {/* Floating Stats */}
            <div className="absolute -top-8 -right-8 bg-white rounded-2xl p-6 shadow-xl hidden md:block" data-aos="zoom-in" data-aos-delay="300">
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">50+</div>
                <div className="text-sm text-gray-600">Happy Families</div>
              </div>
            </div>

            <div className="absolute -bottom-8 -left-8 bg-white rounded-2xl p-6 shadow-xl hidden md:block" data-aos="zoom-in" data-aos-delay="400">
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">8+</div>
                <div className="text-sm text-gray-600">Expert Educators</div>
              </div>
            </div>
          </div>

          {/* Content Side */}
          <div className="space-y-6 lg:space-y-8 w-full" data-aos="fade-left">
            <div className="w-full">
              <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 leading-tight mb-4 lg:mb-6 break-words">
                Our Story –{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">
                  Empowering Young Minds
                </span>{' '}
                for a Brighter Tomorrow
              </h2>
              
              <p className="text-base lg:text-lg text-gray-600 leading-relaxed mb-4 lg:mb-6 break-words">
                THE ASIAN INTERNATIONAL SCHOOL focuses on safe, nurturing, creative learning. 
                We believe every child is unique and deserves individual attention that fosters 
                holistic development – academic, social, and emotional growth in a warm, caring environment.
              </p>

              <p className="text-base lg:text-lg text-gray-600 leading-relaxed break-words">
                Our experienced educators create engaging, play-based learning experiences that 
                build confidence, curiosity, and essential life skills. We partner with parents 
                to ensure each child reaches their full potential.
              </p>
            </div>

            {/* Key Features */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6 w-full">
              <div className="flex items-start space-x-3 lg:space-x-4 min-w-0">
                <div className="w-10 h-10 lg:w-12 lg:h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiHeart} className="w-5 h-5 lg:w-6 lg:h-6 text-emerald-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1 lg:mb-2 break-words">Safe Environment</h4>
                  <p className="text-gray-600 text-sm break-words">Secure, child-friendly facilities with constant supervision</p>
                </div>
              </div>

              <div className="flex items-start space-x-3 lg:space-x-4 min-w-0">
                <div className="w-10 h-10 lg:w-12 lg:h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiAward} className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1 lg:mb-2 break-words">Quality Education</h4>
                  <p className="text-gray-600 text-sm break-words">Research-based curriculum that promotes holistic growth</p>
                </div>
              </div>

              <div className="flex items-start space-x-3 lg:space-x-4 min-w-0">
                <div className="w-10 h-10 lg:w-12 lg:h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiUsers} className="w-5 h-5 lg:w-6 lg:h-6 text-purple-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1 lg:mb-2 break-words">Individual Attention</h4>
                  <p className="text-gray-600 text-sm break-words">Small class sizes ensuring personalized care</p>
                </div>
              </div>

              <div className="flex items-start space-x-3 lg:space-x-4 min-w-0">
                <div className="w-10 h-10 lg:w-12 lg:h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiHeart} className="w-5 h-5 lg:w-6 lg:h-6 text-orange-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1 lg:mb-2 break-words">Nurturing Care</h4>
                  <p className="text-gray-600 text-sm break-words">Warm, loving environment that feels like home</p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="pt-2 lg:pt-4">
              <a
                href="#contact"
                className="inline-flex items-center space-x-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-6 lg:px-8 py-3 lg:py-4 rounded-2xl text-base lg:text-lg font-semibold hover:from-emerald-700 hover:to-teal-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <span>Discover More About Us</span>
                <SafeIcon icon={FiArrowRight} className="w-4 h-4 lg:w-5 lg:h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Story;